import React, { useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useCashieringActions } from '../useCashiering';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { accountDetailsSelectors } from '../../../../data/global/selectors/admin/accountSelectors';
import { DefaultAccountDetails } from '@tradingblock/types';
import { useDispatch } from 'react-redux';
import { AccountActions } from '../../../../data/global/actions/AccountActions';
import { LinkedAccountsActions } from '../../../../data/global/actions/LinkedAccountsActions';
import styled from 'styled-components';

// Add styled container for the filter section
const FilterPanel = styled.div`
  background: var(--blue-dark-lighter);
  border-radius: var(--radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem 1.5rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--lighten);
`;

const FilterLink = styled.span`
  cursor: pointer;
  color: #19bab4;
  text-decoration: underline;
  font-weight: 500;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.4em;
  transition: color 0.15s;
  &:hover {
    color: #13a09a;
    text-decoration: underline;
  }
  i {
    font-size: 1em;
    margin-left: 0.25em;
  }
`;

const SwitchButton = styled.div`
  color: #b3c6e0;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s;
  &:hover {
    color: #fff;
    text-decoration: underline;
  }
`;

export const FilterForAccount: React.FunctionComponent<{
  accountId: number;
  onViewAllEntries?: (accountNumber: string) => void;
}> = ({ accountId, onViewAllEntries }) => {
  const { search: onSearch, setPage, setType, setStatus } = useCashieringActions();
  const accountDetails = useStateSelector(s => accountDetailsSelectors.data(s, accountId));
  const { accountNumber, accountNames } = accountDetails || DefaultAccountDetails;
  const handleSetFilter = useCallback(
    (accountNumberValue: string) => {
      setPage(0);
      setType([]);
      setStatus([]);
      onSearch(accountNumberValue.trim());
      sessionStorage.removeItem('cashieringSearch');
    },
    [accountNumber]
  );

  const history = useHistory();
  const dispatch = useDispatch();

  const accountSelectedCallback = () => {
    dispatch(
      AccountActions.retrieveSetAccountData({
        accountId,
        searchOptions: {
          page: 0,
          pageSize: 5,
          sortBy: '',
          sortByOrder: 'desc',
          search: accountNumber || accountNames[0],
          status: '',
        },
      })
    );
    dispatch(AccountActions.requestSubAccounts({ accountId: accountId }));
    history.push('/');
  };

  const switchToAccount = () => {
    dispatch(
      LinkedAccountsActions.requestLinkedAccounts({
        page: 0,
        pageSize: 5,
        sortBy: '',
        sortByOrder: 'desc',
        search: accountNumber || accountNames[0],
        status: '',
      })
    );
    setTimeout(() => accountSelectedCallback(), 1000);
  };

  return (
    <FilterPanel>
      {onViewAllEntries ? (
        <FilterLink onClick={() => onViewAllEntries(accountNumber || accountNames[0])}>
          View all entries for: {accountNumber || accountNames[0]} <i className="fal fa-filter" />
        </FilterLink>
      ) : (
        <Link
          to="/admin/cashiering"
          onClick={() => handleSetFilter(accountNumber || accountNames[0])}
          style={{ textDecoration: 'none' }}
        >
          <FilterLink>
            View all entries for: {accountNumber || accountNames[0]} <i className="fal fa-filter" />
          </FilterLink>
        </Link>
      )}
      <SwitchButton onClick={switchToAccount}>Switch to Account &gt;&gt;</SwitchButton>
    </FilterPanel>
  );
};
